import React, { memo } from 'react'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { InlineRow } from 'apps/fleet-planner/components/load-details/InlineRow'
import { EQUIPMENT_TYPE, TEMPERATURE_TYPE } from 'apps/fleet-planner/constants/loads'
import { ICustomer, ICustomerContact, ITemperatureSetting } from 'apps/fleet-planner/types'
import { Bullet } from 'components/atoms'
import { Button } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { formatCurrency } from 'utils/formatCurrency'
import formatPhone from 'utils/formatPhone'
import { DISPLAY } from 'constants/display'

interface IEquipmentDetailsProps {
  temperatureSetting?: ITemperatureSetting
  trailerType?: EQUIPMENT_TYPE
  trailerLength?: string
  securingOptions?: string
}

export const EquipmentDetails = memo<IEquipmentDetailsProps>(
  ({ securingOptions, temperatureSetting, trailerLength, trailerType }) => {
    const { fixedTemperature, maxTemperature, minTemperature, precool, temperatureType } =
      temperatureSetting || {}
    const isFixedTemp = temperatureType === 'fixed'
    const formatVanType = (type: string): string => capitalize(type.split('_').join(' '))
    const formatTempType = (tempType: TEMPERATURE_TYPE): string => {
      switch (tempType) {
        case 'fixed':
          return 'Fixed Temperature'
        case 'continuous':
          return 'Continuous Flow'
        default:
          return ''
      }
    }

    return (
      <>
        <span>
          {trailerType ? (
            trailerLength && <span>{trailerLength}&apos; </span>
          ) : (
            <span>{DISPLAY.empty}</span>
          )}
          {trailerType && <span>{formatVanType(trailerType)}</span>}
          {(trailerType || trailerLength) && <br />}
          {trailerType === 'reefer' && (
            <>
              {temperatureType && <span>{formatTempType(temperatureType)}</span>}
              <Bullet />
              {precool && <span>Precool: {parseInt(precool, 10)}&deg;F</span>}
              <Bullet />
              {fixedTemperature && isFixedTemp && (
                <span>Temp: {parseInt(fixedTemperature, 10)}&deg;F</span>
              )}
              {!isFixedTemp && (
                <>
                  {minTemperature ? (
                    <span>Min {parseInt(minTemperature, 10)}&deg;F</span>
                  ) : (
                    <span>No Min</span>
                  )}
                  <Bullet />
                  {maxTemperature ? (
                    <span>Max {parseInt(maxTemperature, 10)}&deg;F</span>
                  ) : (
                    <span>No Max</span>
                  )}
                </>
              )}
            </>
          )}
          {securingOptions && <div>{securingOptions}</div>}
        </span>
      </>
    )
  }
)

interface IMilesProps {
  miles?: number | string
}

export const Miles = memo<IMilesProps>(({ miles }) => {
  return <span data-test='details-miles'>{miles ? `${miles} miles` : DISPLAY.empty} </span>
})
interface IRateProps {
  rate?: string
}

export const Rate = memo<IRateProps>(({ rate }) => {
  return <span data-test='details-rate'>{rate ? formatCurrency(rate) : DISPLAY.empty}</span>
})
interface ILoadIdProps {
  loadId?: string
}

export const LoadId = memo<ILoadIdProps>(({ loadId }) => {
  return <span data-test='details-load-id'>{loadId || DISPLAY.empty}</span>
})

interface ICustomerDetailsProps {
  // load.customer
  customer?: ICustomer
  // load.customerContact
  customerContact?: ICustomerContact
  // load.customerNotes
  customerNotes?: string
  isSharedLoadView?: boolean
  isExternal?: boolean
}

export const CustomerDetails = memo<ICustomerDetailsProps>(
  ({ customer, customerContact, customerNotes, isExternal, isSharedLoadView }) => {
    const { show: liveChatClickHandler } = useChatWidgetContext()

    if (customer?.id || isExternal) {
      return (
        <>
          <div className='b body-medium' data-test='details-customer-name'>
            {customer?.name}
          </div>
          {customerContact && !isExternal && (
            <>
              <span className='body' data-test='customer-contact-information'>
                <span data-test='customer-contact-name'>
                  {customerContact?.name || 'No contact name'}
                </span>
                <Bullet />
                <span className='fs-mask' data-test='customer-contact-phone'>
                  {formatPhone(customerContact?.phone, {
                    removeCountryCode: '+1',
                  }) || 'No phone number'}
                  {customerContact?.phoneExt && (
                    <span className='ml2'>Ext. {customerContact?.phoneExt}</span>
                  )}
                </span>
                <Bullet />
                <span className='fs-mask' data-test='customer-contact-email'>
                  {customerContact?.email || 'No email'}
                </span>
                <br />
              </span>
              <span data-test='details-customer-notes' className='body'>
                {isExternal ? customerNotes : customer?.notes || 'No customer notes'}
              </span>
            </>
          )}

          {isExternal && (
            <span>
              For any updates or issues about this load, please{' '}
              <Button variant='text' onClick={liveChatClickHandler} noPadding>
                Chat
              </Button>{' '}
              with us during business hours or call Carrier Success at (929) 293-0360 for 24/7
              support.{' '}
            </span>
          )}

          {!customerContact && !isExternal && !isSharedLoadView && (
            <span className='body' data-test='customer-contact-information'>
              No customer contact information
              <br />
            </span>
          )}
        </>
      )
    }
    return <div className='b body-medium'>No customer information</div>
  }
)

interface IDetailsProps {
  externalId?: string
  rate?: string
  miles?: string
  trailerType?: EQUIPMENT_TYPE
  trailerLength?: string
  customer?: ICustomer
  customerContact?: ICustomerContact
  temperatureSetting?: ITemperatureSetting
  currentEditField?: string
  isDriverView?: boolean
  isSharedLoadView?: boolean
}

export const Details = memo<IDetailsProps>(
  ({
    customer,
    customerContact,
    externalId,
    isDriverView,
    isSharedLoadView,
    miles,
    rate,
    temperatureSetting,
    trailerLength,
    trailerType,
  }) => {
    const formattedMiles = miles ? parseInt(miles, 10) : ''
    return (
      <>
        <div className='row'>
          <div className='row'>
            <InlineRow
              rowTitle='Customer'
              rowBody={
                <CustomerDetails
                  customer={customer}
                  customerContact={customerContact}
                  isSharedLoadView={isSharedLoadView}
                />
              }
            />
          </div>
          <div className='row'>
            {/* Load ID */}
            <InlineRow rowTitle='Load ID #' rowBody={<LoadId loadId={externalId} />} />
            {/* Rate  */}
            {!isDriverView && <InlineRow rowTitle='Rate' rowBody={<Rate rate={rate} />} />}
            {/* Miles */}
            <InlineRow rowTitle='Miles' rowBody={<Miles miles={formattedMiles} />} />
            {/* Equipment */}
            <InlineRow
              rowTitle='Equipment'
              rowBody={
                <EquipmentDetails
                  trailerType={trailerType}
                  trailerLength={trailerLength}
                  temperatureSetting={temperatureSetting}
                />
              }
            />
          </div>
        </div>
      </>
    )
  }
)
